import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import AuthRoutes from 'app/Auth/Routes';
import PrivacyPolicy from 'app/PrivacyPolicy';
import PaymentTerms from 'app/PaymentTerms';
import Terms from 'app/Terms';
import asyncComponent from 'shared/utilities/asyncComponent';
import { Head } from 'shared/components/Head';
import { EnvironmentHeader } from 'shared/components/EnvironmentHeader';
import { Session } from 'shared/components/Session';
import { ShardProvider } from 'shared/components/ShardProvider';
import { LoginDispatch } from 'app/LoginDispatch';
import { MaintenanceGuard } from 'shared/components/MaintenanceMode';
import { LOCATION_CHANGE } from 'shared/store/modules/router/actions';

import { getAuth0Config } from 'shared/store/modules/provider/selectors';

import { useSelector } from 'react-redux';

const ResidentialRoutes = asyncComponent(() => import('app/Residential').then(module => module.default));
const SignupRoutes = asyncComponent(() => import('app/Signup').then(module => module.default));

// Dispatches LOCATION_CHANGE on every navigation so reducers that reset
// state on route change (nav close, error clear, etc.) continue to work.
function LocationChangeDispatcher() {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: LOCATION_CHANGE, payload: { location } });
  }, [location, dispatch]);

  return null;
}

const Auth0Inner = () => {
  const auth0Config = useSelector(getAuth0Config);
  const navigate = useNavigate();

  const onLogin = (appState?: AppState) => {
    if (window.location.pathname !== '/signup/account-details') {
      navigate(appState?.returnTo || '/after-login');
    }
  };

  const auth0Options = {
    domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
    clientId: auth0Config.clientId,
    cacheLocation: 'localstorage' as const,
    onRedirectCallback: onLogin,
    useRefreshTokens: true,
    authorizationParams: {
      organization: auth0Config.organizationId,
      redirect_uri: window.location.origin,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE || ''
    }
  };

  return (
    <Auth0Provider {...auth0Options}>
      <LocationChangeDispatcher />
      <Routes>
        {AuthRoutes.map((route, index) => (
          <Route element={<route.component />} key={index} path={route.path} />
        ))}
        <Route element={<ResidentialRoutes />} path="/residential/*" />
        <Route element={<SignupRoutes />} path="/signup/*" />
        <Route element={<LoginDispatch />} path="/after-login" />
        <Route element={<PrivacyPolicy />} path="/privacy" />
        <Route element={<Terms />} path="/terms" />
        <Route element={<PaymentTerms />} path="/payment-terms" />
        <Route element={<Session />} path="/*" />
      </Routes>
    </Auth0Provider>
  );
};

export function App() {
  return (
    <MaintenanceGuard>
      <ShardProvider>
        <Head />
        <EnvironmentHeader />
        <BrowserRouter>
          <Auth0Inner />
        </BrowserRouter>
      </ShardProvider>
    </MaintenanceGuard>
  );
}
